import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';

import SelectedEntitiesContainer from '../common/SelectedEntitiesContainer';
import SearchContainer from '../common/SearchContainer';
import IpInputContainer from '../common/IpInputContainer';
import UnmanagedInputContainer from '../common/UnmanagedInputContainer';

import CircleRadio from '../../../Controls/Buttons/CircleRadio/CircleRadio';

import { SELECTOR_TYPE } from '../common/selectorsConsts';
import { AD_ENTITY_TYPES, SELECTOR_TYPES } from '@silverfort-nodelibs/common/utils/consts';

import styles from './ADEntitiesSelector.module.css';
import { addIpsFromFile } from '../../../utils';

const ENTITIES_DATA_TYPES = {
    OBJECTS: { value: 1, label: '' },
    IPS: { value: 2, label: 'IPs' },
    UNMANAGED: { value: 3, label: '' },
    APPLICATIONS: { value: 4, label: 'Applications' }
};

const ADEntitiesSelector = ({
    selectorType,
    modalTitle,
    selectedObjectEntities,
    showModal,
    handleCloseModal,
    saveEntities,
    showExcluded,
    disableAdd = false,
    footerMsg,
    allowIps,
    allowApplications,
    disableNetMasks,
    allowUnmanagedEntities,
    selectedIpEntities,
    selectedApplicationEntities,
    selectedUnmanagedEntities,
    removeEntitiesBlocked,
    isEditor = true,
    showSelectAll,
    allowSelectAll,
    selectAllText,
    allEntitiesSelected,
    maxEntitySelection,
    maxFileSize,
    maxFileSizeText,
    authTypes,
    footerComponent,
    ipOnly,
    disableAutoComplete
}) => {
    const showDataTypesFilter = allowIps || allowUnmanagedEntities;
    const [currentObjectEntities, setCurrentObjectEntities] = useState([...selectedObjectEntities]);
    const [currentIpEntities, setCurrentIpEntities] = useState([
        ...(allowIps && selectedIpEntities ? selectedIpEntities : []).map((ipObj) => {
            return { ...ipObj, type: AD_ENTITY_TYPES.IP };
        })
    ]);
    const [currentApplicationEntities, setCurrentApplicationEntities] = useState([
        ...(allowApplications && selectedApplicationEntities ? selectedApplicationEntities : []).map((application) => {
            return { ...application, type: AD_ENTITY_TYPES.APPLICATION };
        })
    ]);
    const [currentUnmanagedEntities, setCurrentUnmanagedEntities] = useState([
        ...(selectedUnmanagedEntities ? selectedUnmanagedEntities : []).map((host) => {
            return { ...host, type: selectorType.entityType, unmanaged: true };
        })
    ]);

    const [isMaxEntitiesReached, setIsMaxEntitiesReached] = useState(false);

    const [currentEntitiesDataTypes, setCurrentEntitiesDataTypes] = useState(
        ipOnly ? ENTITIES_DATA_TYPES.IPS.value : ENTITIES_DATA_TYPES.OBJECTS.value
    );
    const [selectAllEntities, setSelectAllEntities] = useState(allEntitiesSelected === true);

    const [fileLoading, setFileLoading] = useState(false);
    const [fileError, setFileError] = useState('');

    useEffect(() => {
        if (maxEntitySelection) {
            const isMaxReached =
                currentObjectEntities.length +
                    currentIpEntities.length +
                    currentApplicationEntities.length +
                    currentUnmanagedEntities.length >=
                maxEntitySelection;
            setIsMaxEntitiesReached(isMaxReached);
        }
    }, [
        currentObjectEntities,
        currentIpEntities,
        currentApplicationEntities,
        currentUnmanagedEntities,
        maxEntitySelection
    ]);

    const onRemoveSelectedObject = (index) => {
        const newSelectedObjectEntities = [...currentObjectEntities];
        const removedEntity = { ...newSelectedObjectEntities[index] };
        removedEntity.is_protected = false;

        newSelectedObjectEntities.splice(index, 1);
        setCurrentObjectEntities(newSelectedObjectEntities);
    };

    const onRemoveSelectedIp = (index) => {
        const newSelectedIps = [...currentIpEntities];
        newSelectedIps.splice(index, 1);
        setCurrentIpEntities(newSelectedIps);
    };

    const onRemoveSelectedApplication = (index) => {
        const newSelectedApplications = [...currentApplicationEntities];
        newSelectedApplications.splice(index, 1);
        setCurrentApplicationEntities(newSelectedApplications);
    };

    const onRemoveSelectedUnmanaged = (index) => {
        const newSelectedUnmanagedEntities = [...currentUnmanagedEntities];
        newSelectedUnmanagedEntities.splice(index, 1);
        setCurrentUnmanagedEntities(newSelectedUnmanagedEntities);
    };

    const onRemoveSelected = (entityIndex, entity) => {
        if (entity.type === AD_ENTITY_TYPES.IP) {
            const ipEntityIndex = currentIpEntities.findIndex((ipEntity) => {
                return ipEntity.mask === entity.mask && ipEntity.address === entity.address;
            });
            if (ipEntityIndex >= 0) {
                onRemoveSelectedIp(ipEntityIndex);
            }
        } else if (entity.unmanaged) {
            const unmanagedEntityIndex = currentUnmanagedEntities.findIndex((unmanagedEntity) => {
                return unmanagedEntity.name === entity.name;
            });
            if (unmanagedEntityIndex >= 0) {
                onRemoveSelectedUnmanaged(unmanagedEntityIndex);
            }
        } else if (entity.applicationEntity) {
            const objectEntityIndex = currentApplicationEntities.findIndex((objectEntity) => {
                return objectEntity.guid === entity.guid;
            });
            if (objectEntityIndex >= 0) {
                onRemoveSelectedApplication(objectEntityIndex);
            }
        } else {
            const objectEntityIndex = currentObjectEntities.findIndex((objectEntity) => {
                return objectEntity.guid === entity.guid;
            });
            if (objectEntityIndex >= 0) {
                onRemoveSelectedObject(objectEntityIndex);
            }
        }
    };

    const onSelectAllEntitiesClicked = (newState) => {
        if (newState) {
            const newSelectedObjectEntities = currentObjectEntities.filter((entity) => entity.excluded);
            setCurrentObjectEntities(newSelectedObjectEntities);
        }
        setSelectAllEntities(newState);
    };

    const addObjectEntity = (suggestion, excluded, event) => {
        event.stopPropagation();
        const newSelectedObjectEntities = [...currentObjectEntities];
        suggestion.excluded = excluded;
        suggestion.is_protected = true;
        newSelectedObjectEntities.push(suggestion);
        setCurrentObjectEntities(newSelectedObjectEntities);
    };

    const addIpEntity = (newIp) => {
        let isAdded = false;
        setCurrentIpEntities((currentIpEntities) => {
            const sameSelected = currentIpEntities.filter((selectedIp) =>
                disableNetMasks
                    ? selectedIp.address === newIp.address || selectedIp.identifier === newIp.address
                    : (selectedIp.address === newIp.address && selectedIp.mask === newIp.mask) ||
                      selectedIp.identifier === `${newIp.address}/${newIp.mask}`
            );
            if (sameSelected.length > 0) {
                return currentIpEntities;
            }
            isAdded = true;
            return [...currentIpEntities, newIp];
        });

        return isAdded;
    };

    const addApplicationEntity = (suggestion, excluded, event) => {
        event.stopPropagation();
        const newSelectedObjectEntities = [...currentApplicationEntities];
        suggestion.excluded = excluded;
        suggestion.is_protected = true;
        suggestion.applicationEntity = true;
        newSelectedObjectEntities.push(suggestion);
        setCurrentApplicationEntities(newSelectedObjectEntities);
    };

    const addUnmanagedEntity = (unmanagedEntity) => {
        const sameSelected = currentUnmanagedEntities.filter(
            (selectedUnmanaged) => selectedUnmanaged.name === unmanagedEntity.name
        );
        if (sameSelected.length > 0) {
            return false;
        }

        const newSelectedUnmanagedEntities = [...currentUnmanagedEntities];
        newSelectedUnmanagedEntities.push(unmanagedEntity);
        setCurrentUnmanagedEntities(newSelectedUnmanagedEntities);
        return true;
    };

    const closeSelector = () => {
        if (handleCloseModal) {
            handleCloseModal();
        }
    };

    const onSaveEntitiesClicked = () => {
        saveEntities({
            objectEntities: currentObjectEntities,
            ipEntities: currentIpEntities,
            unmanagedEntities: currentUnmanagedEntities,
            selectAllEntities: selectAllEntities,
            applicationEntities: currentApplicationEntities
        });
    };

    const entitiesDataOptions = [];
    if (!ipOnly) {
        entitiesDataOptions.push({ ...ENTITIES_DATA_TYPES.OBJECTS, label: selectorType.managedSelectorTitle });
    }
    if (allowIps) {
        entitiesDataOptions.push(ENTITIES_DATA_TYPES.IPS);
    }
    if (allowApplications) {
        entitiesDataOptions.push(ENTITIES_DATA_TYPES.APPLICATIONS);
    }
    if (allowUnmanagedEntities) {
        entitiesDataOptions.push({ ...ENTITIES_DATA_TYPES.UNMANAGED, label: selectorType.unmanagedSelectorTitle });
    }

    const onFileUploaded = (file, exclude = false) => {
        addIpsFromFile(file, addIpEntity, setFileError, setFileLoading, maxFileSize, maxFileSizeText, exclude);
    };

    return (
        <div className={styles.modalContainer}>
            <Modal
                id={styles.adEntitiesSelectorModal}
                show={showModal}
                onHide={closeSelector}
                className={'z-[10000]'}
                backdropClassName="z-[9000]"
                keyboard={true}
                backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle ? modalTitle : selectorType.modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.selectDiv}>
                        {showDataTypesFilter && (
                            <div className={styles.dataTypesFilter}>
                                <div className={styles.labelText}>Type:</div>

                                <CircleRadio
                                    handleCircleRadioChange={(event) =>
                                        setCurrentEntitiesDataTypes(parseInt(event.target.value))
                                    }
                                    valuesList={Object.values(entitiesDataOptions)}
                                    radioValue={currentEntitiesDataTypes}
                                />
                            </div>
                        )}

                        {isEditor && currentEntitiesDataTypes === ENTITIES_DATA_TYPES.OBJECTS.value && (
                            <SearchContainer
                                selectorData={selectorType}
                                onSelectSuggestion={addObjectEntity}
                                showExcluded={showExcluded}
                                disableAdd={disableAdd}
                                currentSelectedEntities={currentObjectEntities}
                                selectAllEntities={selectAllEntities}
                                allowSelectAll={allowSelectAll}
                                onSelectAllEntitiesClicked={showSelectAll ? onSelectAllEntitiesClicked : null}
                                selectAllText={selectAllText}
                                allowChangeDisplayAccountType={selectorType.value !== SELECTOR_TYPES.APPLICATIONS.value}
                                isMaxEntitiesReached={isMaxEntitiesReached}
                                alwaysSearch={selectorType.value === SELECTOR_TYPES.APPLICATIONS.value}
                                authTypes={authTypes}
                            />
                        )}
                        {isEditor && currentEntitiesDataTypes === ENTITIES_DATA_TYPES.IPS.value && (
                            <IpInputContainer
                                disableNetMasks={disableNetMasks}
                                addIp={addIpEntity}
                                showExcluded={showExcluded}
                                disabledAdd={disableAdd}
                                disableAutoComplete={disableAutoComplete}
                            />
                        )}
                        {isEditor && currentEntitiesDataTypes === ENTITIES_DATA_TYPES.UNMANAGED.value && (
                            <UnmanagedInputContainer
                                entityType={selectorType.entityType}
                                addUnmanagedEntity={addUnmanagedEntity}
                                showExcluded={showExcluded}
                                disableAdd={disableAdd}
                            />
                        )}
                        {isEditor && currentEntitiesDataTypes === ENTITIES_DATA_TYPES.APPLICATIONS.value && (
                            <SearchContainer
                                selectorData={SELECTOR_TYPES.APPLICATIONS}
                                onSelectSuggestion={addApplicationEntity}
                                showExcluded={showExcluded}
                                disableAdd={disableAdd}
                                currentSelectedEntities={currentApplicationEntities}
                                selectAllEntities={false}
                                allowSelectAll={false}
                                onSelectAllEntitiesClicked={null}
                                selectAllText={selectAllText}
                                allowChangeDisplayAccountType={false}
                                isMaxEntitiesReached={isMaxEntitiesReached}
                                alwaysSearch={false}
                            />
                        )}
                    </div>
                    <SelectedEntitiesContainer
                        selectedEntities={[
                            ...currentObjectEntities,
                            ...currentIpEntities,
                            ...currentUnmanagedEntities,
                            ...currentApplicationEntities
                        ]}
                        onRemoveSelected={onRemoveSelected}
                        removeIncludedBlocked={removeEntitiesBlocked}
                        removeExcludedBlocked={removeEntitiesBlocked}
                        selectorType={SELECTOR_TYPE.ENTITY}
                        selectAllEntities={selectAllEntities}
                        selectAllText={selectAllText}
                        onFileUploaded={
                            isEditor && currentEntitiesDataTypes === ENTITIES_DATA_TYPES.IPS.value
                                ? onFileUploaded
                                : null
                        }
                        fileLoading={fileLoading}
                        showExcluded={showExcluded}
                        showSelected={!disableAdd}
                    />
                    {fileError && currentEntitiesDataTypes === ENTITIES_DATA_TYPES.IPS.value && (
                        <div className={styles.error}>
                            <span>{fileError}</span>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex flex-col w-full">
                        {!!footerComponent && footerComponent}
                        {footerMsg && <div className={styles.footerMsg}>{footerMsg}</div>}
                        <div className={styles.footerButtons}>
                            <Button onClick={closeSelector}>Cancel</Button>
                            <Button bsStyle="success" onClick={onSaveEntitiesClicked} disabled={!isEditor}>
                                Done
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ADEntitiesSelector;
